//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Moment } from 'moment-timezone';

import { TScheduledPostConfig } from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import {
  isScheduledOrganicPinConfig,
  isTikTokPhotoAutomaticPostConfig,
  isYouTubeAutomaticPostConfig,
} from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import {
  deleteDraftPosts,
  getDraftPosts as getDraftPostsByIds,
  searchDraftPosts,
  updateDraftPost,
} from '@apis/universal-posts/draft-posts/universalDraftPostsApi';
import { IDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import {
  deleteScheduledPosts,
  getScheduledPost,
  searchScheduledPosts,
  updateScheduledPost,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import { IScheduledPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { UniversalPostingGroupName } from '@apis/universal-posts/universalPostsApiModel';
import {
  getSocialNetworkFromPostType,
  isDraftPost,
  isScheduledPost,
} from '@apis/universal-posts/universalPostsUtils';
import * as userSettingsApi from '@apis/user-settings/userSettingsApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import DateString from '@models/DateString';
import DraftId from '@models/DraftId';
import PinterestBoardId from '@models/PinterestBoardId';
import PinterestBoardSectionId from '@models/PinterestBoardSectionId';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import { SocialNetwork } from '@models/SocialNetwork';
import TimeZoneId from '@models/TimeZoneId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { PostDetailsValidationError } from '@universal-scheduling/components/calendar/UniversalCalendarConstants';
import { invalidateFeedPreviewScheduledInstagramPostsQuery } from '@universal-scheduling/components/calendar/feed-preview/scheduleFeedPreviewUtils';
import { getStartOfCurrentMonth } from '@universal-scheduling/components/calendar/month/CalendarMonthViewUtils';
import {
  getCurrentContentTypeFilter,
  getCurrentDateRange,
  getCurrentNetworkFilter,
  getCurrentPostInModal,
  getCurrentPostStatusFilter,
  getCurrentPostStatusesForRequest,
  getCurrentPostingGroupsForRequest,
  getCurrentStartDate,
  getDraftPosts as getDraftPostsSelector,
  getModalValidationErrors,
  getScheduleView,
  getScheduledPosts as getScheduledPostsSelector,
  getStartOfWeek,
} from '@universal-scheduling/redux/calendar/selectors/getUniversalCalendarSettings';
import {
  ContentTypeFilter,
  IScheduleCalendarSettings,
  IUniversalCalendarState,
  NormalizedDraftPosts,
  NormalizedScheduledPosts,
  PostStatusFilter,
  SCHEDULED_POST_FETCH_LIMIT,
  UniversalCalendarView,
} from '@universal-scheduling/redux/calendar/universalCalendarModel';
import {
  getHasPostedTimeError,
  getViewDates,
  updateCalendarNetworkFilterParam,
  updateCalendarUrlParams,
} from '@universal-scheduling/redux/calendar/universalCalendarUtils';
import { difference } from '@utils/arrayUtils';
import { isDefined } from '@utils/filterPredicates';
import { momentFromDate } from '@utils/moment';
import normalize, { Normalized } from '@utils/normalize';

const initialState: IUniversalCalendarState = {
  scheduleView: UniversalCalendarView.Month,
  scheduledPosts: Normalized.empty(),
  draftPosts: Normalized.empty(),

  availableNetworks: [],
  disconnectedAvailableNetworks: [],

  startOfWeek: 0,

  searchText: '',
  contentTypeFilter: 'All',
  networkFilter: 'All',
  postStatusFilter: 'All',

  dateRange: {
    startDate: DateString.getToday(TimeZoneId.Utc),
    endDate: DateString.addDays(DateString.getToday(TimeZoneId.Utc), 6),
  },

  modals: {
    isDeletePostModalOpen: false,
    isPostDetailsModalOpen: false,
    currentPost: undefined,
    editedPost: undefined,
    createdPosts: undefined,
    previewCuidIndex: 0,
    validationErrors: [],
  },
  flags: {
    isDeletingPost: false,
    isFetchingCalendarSettings: false,
    isFetchingScheduledPosts: false,
    isSettingCalendarSettings: false,
    isUpdatingPostScheduledTime: false,
    isUpdatingPostDetails: false,
  },
  errors: {
    deletePostError: undefined,
    fetchCalendarSettingsError: undefined,
    fetchScheduledPostsError: undefined,
    setCalendarSettingsError: undefined,
    updatePostScheduledTimeError: undefined,
    updatePostDetailsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.UniversalCalendar,
  initialState,
  reducers: {
    setDateRange(
      state,
      action: PayloadAction<{ startDate: DateString; endDate: DateString }>,
    ) {
      state.dateRange = action.payload;
    },
    setScheduleView(state, action: PayloadAction<UniversalCalendarView>) {
      state.scheduleView = action.payload;
    },
    setAvailableNetworks(state, action: PayloadAction<SocialNetwork[]>) {
      state.availableNetworks = action.payload;
    },
    setDisonnectedAvailableNetworks(
      state,
      action: PayloadAction<SocialNetwork[]>,
    ) {
      state.disconnectedAvailableNetworks = action.payload;
    },
    setNetworkFilter(state, action: PayloadAction<SocialNetwork | 'All'>) {
      state.networkFilter = action.payload;
    },
    setPostStatusFilter(
      state,
      action: PayloadAction<PostStatusFilter | 'All'>,
    ) {
      state.postStatusFilter = action.payload;
    },
    setContentTypeFilter(
      state,
      action: PayloadAction<ContentTypeFilter | 'All'>,
    ) {
      state.contentTypeFilter = action.payload;
    },
    setSearchText(state, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },

    requestScheduledPosts(state) {
      state.errors.fetchScheduledPostsError = undefined;
      state.flags.isFetchingScheduledPosts = true;
    },
    receiveScheduledPostsSucceeded(
      state,
      action: PayloadAction<{
        scheduledPosts: NormalizedScheduledPosts;
        draftPosts: NormalizedDraftPosts;
      }>,
    ) {
      state.flags.isFetchingScheduledPosts = false;

      state.scheduledPosts = Normalized.addItems(
        state.scheduledPosts,
        action.payload.scheduledPosts,
      );
      state.draftPosts = Normalized.addItems(
        state.draftPosts,
        action.payload.draftPosts,
      );
    },
    receiveScheduledPostsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingScheduledPosts = false;
      state.errors.fetchScheduledPostsError = action.payload;
    },
    clearPostsAndDrafts(state) {
      state.scheduledPosts = Normalized.empty();
      state.draftPosts = Normalized.empty();
    },
    receiveDraftPostByIdSucceeded(state, action: PayloadAction<IDraftPost>) {
      const editedPost = state.modals.editedPost;

      state.flags.isFetchingScheduledPosts = false;
      state.draftPosts = Normalized.addItem(
        state.draftPosts,
        action.payload.id,
        action.payload,
      );

      if (editedPost && isDraftPost(editedPost)) {
        state.modals.editedPost = {
          ...editedPost,
          mediaItems: action.payload.mediaItems,
        };
      }
    },
    receiveScheduledPostByIdSucceeded(
      state,
      action: PayloadAction<IScheduledPost>,
    ) {
      const editedPost = state.modals.editedPost;

      state.flags.isFetchingScheduledPosts = false;
      state.scheduledPosts = Normalized.addItem(
        state.scheduledPosts,
        action.payload.id,
        action.payload,
      );

      if (editedPost && isScheduledPost(editedPost)) {
        state.modals.editedPost = {
          ...editedPost,
          mediaItems: action.payload.mediaItems,
        };
      }
    },
    requestUpdatePostScheduledTime(
      state,
      {
        payload,
      }: PayloadAction<{
        post: IScheduledPost | IDraftPost;
        newScheduledTime: Date;
      }>,
    ) {
      state.errors.updatePostScheduledTimeError = undefined;
      state.flags.isUpdatingPostScheduledTime = true;
      if (isScheduledPost(payload.post)) {
        state.scheduledPosts = Normalized.addItem(
          state.scheduledPosts,
          payload.post.id,
          { ...payload.post, scheduledTime: payload.newScheduledTime },
        );
      } else {
        state.draftPosts = Normalized.addItem(
          state.draftPosts,
          payload.post.id,
          { ...payload.post, scheduledTime: payload.newScheduledTime },
        );
      }
    },
    receiveUpdatePostScheduledTimeSucceeded(state) {
      state.flags.isUpdatingPostScheduledTime = false;
    },
    receiveUpdatePostScheduledTimeFailed(
      state,
      {
        payload,
      }: PayloadAction<{
        post: IScheduledPost | IDraftPost;
        errorMessage: string | undefined;
      }>,
    ) {
      state.flags.isUpdatingPostScheduledTime = false;
      state.errors.updatePostScheduledTimeError = payload.errorMessage;
      if (isScheduledPost(payload.post)) {
        state.scheduledPosts = Normalized.addItem(
          state.scheduledPosts,
          payload.post.id,
          payload.post,
        );
      } else {
        state.draftPosts = Normalized.addItem(
          state.draftPosts,
          payload.post.id,
          payload.post,
        );
      }
    },
    requestUpdatePostDetails(state) {
      state.errors.updatePostDetailsError = undefined;
      state.flags.isUpdatingPostDetails = true;
    },
    receiveUpdatePostDetailsSucceeded(state) {
      state.flags.isUpdatingPostDetails = false;
    },
    receiveUpdatePostDetailsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isUpdatingPostDetails = false;
      state.errors.updatePostDetailsError = action.payload;
    },

    requestDeletePost(state, { payload: post }: PayloadAction<IScheduledPost>) {
      state.errors.deletePostError = undefined;
      state.flags.isDeletingPost = true;
      state.scheduledPosts = Normalized.removeItem(
        state.scheduledPosts,
        post.id,
      );
    },
    receiveDeletePostSucceeded(state) {
      state.flags.isDeletingPost = false;
    },
    receiveDeletePostFailed(
      state,
      {
        payload,
      }: PayloadAction<{
        post: IScheduledPost;
        errorMessage: string | undefined;
      }>,
    ) {
      state.flags.isDeletingPost = false;
      state.errors.deletePostError = payload.errorMessage;
      state.scheduledPosts = Normalized.addItem(
        state.scheduledPosts,
        payload.post.id,
        payload.post,
      );
    },

    requestDeleteDraftPost(
      state,
      { payload: draft }: PayloadAction<IDraftPost>,
    ) {
      state.errors.deletePostError = undefined;
      state.flags.isDeletingPost = true;
      state.draftPosts = Normalized.removeItem(state.draftPosts, draft.id);
    },
    receiveDeleteDraftPostSucceeded(state) {
      state.flags.isDeletingPost = false;
    },
    receiveDeleteDraftPostFailed(
      state,
      { payload }: PayloadAction<{ draft: IDraftPost; errorMessage: string }>,
    ) {
      state.flags.isDeletingPost = false;
      state.errors.deletePostError = payload.errorMessage;
      state.draftPosts = Normalized.addItem(
        state.draftPosts,
        payload.draft.id,
        payload.draft,
      );
    },

    requestCalendarSettings(state) {
      state.errors.fetchCalendarSettingsError = undefined;
      state.flags.isFetchingCalendarSettings = true;
    },
    receiveCalendarSettingsSucceeded(
      state,
      action: PayloadAction<IScheduleCalendarSettings>,
    ) {
      if (isDefined(action.payload.scheduleCalendarStartOfWeek)) {
        state.startOfWeek = action.payload.scheduleCalendarStartOfWeek;
      }
      if (isDefined(action.payload.scheduleCalendarView)) {
        state.scheduleView = action.payload.scheduleCalendarView;
      }
      state.flags.isFetchingCalendarSettings = false;
    },
    receiveCalendarSettingsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingCalendarSettings = false;
      state.errors.fetchCalendarSettingsError = action.payload;
    },

    requestSetCalendarSettings(
      state,
      action: PayloadAction<IScheduleCalendarSettings>,
    ) {
      if (isDefined(action.payload.scheduleCalendarStartOfWeek)) {
        state.startOfWeek = action.payload.scheduleCalendarStartOfWeek;
      }
      if (isDefined(action.payload.scheduleCalendarView)) {
        state.scheduleView = action.payload.scheduleCalendarView;
      }
      state.errors.setCalendarSettingsError = undefined;
      state.flags.isSettingCalendarSettings = true;
    },
    receiveSetCalendarSettingsSucceeded(state) {
      state.flags.isSettingCalendarSettings = false;
    },
    receiveSetCalendarSettingsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isSettingCalendarSettings = false;
      state.errors.setCalendarSettingsError = action.payload;
    },
    openPostDetailsModal(
      state,
      action: PayloadAction<IScheduledPost | IDraftPost | undefined>,
    ) {
      state.modals.isPostDetailsModalOpen = true;
      state.modals.currentPost = action.payload;
      state.modals.editedPost = action.payload;
    },
    closePostDetailsModal(state) {
      state.modals.isPostDetailsModalOpen = false;
      state.modals.currentPost = undefined;
      state.modals.editedPost = undefined;
      state.modals.previewCuidIndex = 0;
    },
    setCurrentPostInModalPreviewCuidIndex(
      state,
      action: PayloadAction<number>,
    ) {
      state.modals.previewCuidIndex = action.payload;
    },
    setValidationErrors(
      state,
      action: PayloadAction<PostDetailsValidationError[]>,
    ) {
      state.modals.validationErrors = action.payload;
    },
    editPostScheduledTime(state, action: PayloadAction<Date>) {
      const post = state.modals.currentPost;
      const hasDateError =
        isDefined(post) &&
        isScheduledPost(post) &&
        momentFromDate(action.payload, TimeZoneId.Utc).isBefore(
          momentFromDate(new Date(), TimeZoneId.Utc),
        );

      if (state.modals.editedPost) {
        if (hasDateError) {
          state.modals.validationErrors.push(
            PostDetailsValidationError.PostTimeInPast,
          );
        } else {
          state.modals.validationErrors = difference(
            state.modals.validationErrors,
            [PostDetailsValidationError.PostTimeInPast],
          );
        }
        state.modals.editedPost.scheduledTime = action.payload;
      }
    },
    editPostConfig(
      state,
      action: PayloadAction<{
        config: TScheduledPostConfig;
        hasError: boolean;
      }>,
    ) {
      if (isDefined(state.modals.editedPost)) {
        if (action.payload.hasError) {
          state.modals.validationErrors.push(
            PostDetailsValidationError.CaptionTooLong,
          );
          state.modals.editedPost.config = action.payload.config;
        } else {
          state.modals.validationErrors = difference(
            state.modals.validationErrors,
            [PostDetailsValidationError.CaptionTooLong],
          );
          state.modals.editedPost.config = action.payload.config;
        }
      }
    },
    editPinterestBoard(
      state,
      action: PayloadAction<{
        newBoardId: PinterestBoardId;
        newSectionId?: PinterestBoardSectionId;
      }>,
    ) {
      const { newBoardId, newSectionId } = action.payload;

      if (
        isDefined(state.modals.editedPost) &&
        isScheduledOrganicPinConfig(state.modals.editedPost.config)
      ) {
        state.modals.editedPost.config.boardId = newBoardId;
        state.modals.editedPost.config.boardSection = newSectionId;
      }
    },
    editTitle(
      state,
      action: PayloadAction<{ title: string; hasError: boolean }>,
    ) {
      if (isDefined(state.modals.editedPost)) {
        if (
          isScheduledOrganicPinConfig(state.modals.editedPost.config) ||
          isYouTubeAutomaticPostConfig(state.modals.editedPost.config) ||
          isTikTokPhotoAutomaticPostConfig(state.modals.editedPost.config)
        ) {
          const { hasError, title } = action.payload;

          if (hasError) {
            state.modals.validationErrors.push(
              PostDetailsValidationError.TitleTooLong,
            );
          } else {
            state.modals.validationErrors = difference(
              state.modals.validationErrors,
              [PostDetailsValidationError.TitleTooLong],
            );
          }
          state.modals.editedPost.config.title = title;
        }
      }
    },
    openDeletePostModal(
      state,
      action: PayloadAction<IScheduledPost | IDraftPost>,
    ) {
      state.modals.currentPost = action.payload;
      state.modals.isDeletePostModalOpen = true;
    },
    closeDeletePostModal(state) {
      state.modals.isDeletePostModalOpen = false;
    },
    setSchedulerCreatedPosts(
      state,
      action: PayloadAction<(IScheduledPost | IDraftPost)[]>,
    ) {
      state.modals.createdPosts = action.payload;
    },
    clearSchedulerCreatedPosts(state) {
      state.modals.createdPosts = undefined;
    },
  },
});

export const {
  clearPostsAndDrafts,
  clearSchedulerCreatedPosts,
  closeDeletePostModal,
  closePostDetailsModal,
  editPinterestBoard,
  editPostConfig,
  editPostScheduledTime,
  editTitle,
  openDeletePostModal,
  openPostDetailsModal,
  receiveUpdatePostScheduledTimeFailed,
  receiveUpdatePostScheduledTimeSucceeded,
  requestUpdatePostScheduledTime,
  setAvailableNetworks,
  setContentTypeFilter,
  setCurrentPostInModalPreviewCuidIndex,
  setDisonnectedAvailableNetworks,
  setPostStatusFilter,
  setScheduleView,
  setSchedulerCreatedPosts,
  setSearchText,
  setValidationErrors,
} = actions;

export const UniversalCalendarReducer = reducer;

export const openPostDetailsModalByDraftId = createThunk(
  (draftId: DraftId) => async (dispatch, getState) => {
    const drafts = getDraftPostsSelector(getState());
    const draftFromState = Normalized.getById(drafts, draftId);

    if (isDefined(draftFromState)) {
      dispatch(actions.openPostDetailsModal(draftFromState));
    } else {
      try {
        const draft = (await getDraftPostsByIds([draftId]))[0];

        dispatch(actions.openPostDetailsModal(draft));
      } catch {
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Unable to Find Draft',
            type: ToastType.ERROR,
            message:
              'Unable to find draft. Please try again later or contact Bazaarvoice Support.',
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );
      }
    }
  },
);

export const openPostDetailsModalById = createThunk(
  (postId: PostableId) => async (dispatch, getState) => {
    const posts = getScheduledPostsSelector(getState());
    const postFromState = Normalized.getById(posts, postId);

    if (isDefined(postFromState)) {
      dispatch(actions.openPostDetailsModal(postFromState));
    } else {
      try {
        const post = await getScheduledPost(postId);

        if (post.status === PostableStatus.Cancelled) {
          dispatch(
            toastNotificationsActions.addToastNotification({
              title: 'Post Deleted',
              type: ToastType.ERROR,
              message: 'This post was previously deleted.',
              options: {
                toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
              },
            }),
          );
        } else {
          dispatch(actions.openPostDetailsModal(post));
        }
      } catch {
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Unable to Find Post',
            type: ToastType.ERROR,
            message:
              'Unable to find post. Please try again later or contact Bazaarvoice Support.',
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );
      }
    }
  },
);

export const resetPostDetailsModal = createThunk(() => (dispatch) => {
  dispatch(actions.closePostDetailsModal());
  dispatch(actions.setValidationErrors([]));
});

export const setNetworkFilter = createThunk(
  (newNetworkFilter: SocialNetwork | 'All') => (dispatch) => {
    updateCalendarNetworkFilterParam(newNetworkFilter);
    dispatch(actions.setNetworkFilter(newNetworkFilter));
  },
);

export const setNetworkFilterAndFetch = createThunk(
  (newNetworkFilter: SocialNetwork | 'All') => (dispatch, getState) => {
    const { endDate, startDate } = getCurrentDateRange(getState());

    dispatch(setNetworkFilter(newNetworkFilter));
    void dispatch(setDateRangeAndFetchPosts(startDate, endDate, true));
  },
);

export const setPostStatusFilterAndFetch = createThunk(
  (newPostStatusFilter: PostStatusFilter | 'All') => (dispatch, getState) => {
    const state = getState();
    const postStatusFilter = getCurrentPostStatusFilter(state);
    const { endDate, startDate } = getCurrentDateRange(state);

    dispatch(actions.setPostStatusFilter(newPostStatusFilter));

    if (
      postStatusFilter !== newPostStatusFilter &&
      (newPostStatusFilter !== PostStatusFilter.Draft ||
        postStatusFilter === PostStatusFilter.Manual)
    ) {
      void dispatch(setDateRangeAndFetchPosts(startDate, endDate, true));
    }
  },
);

export const setContentTypeFilterAndFetch = createThunk(
  (newContentTypeFilter: ContentTypeFilter | 'All') => (dispatch, getState) => {
    const contentTypeFilter = getCurrentContentTypeFilter(getState());
    const { endDate, startDate } = getCurrentDateRange(getState());

    dispatch(actions.setContentTypeFilter(newContentTypeFilter));
    if (
      newContentTypeFilter !== contentTypeFilter &&
      (newContentTypeFilter === ContentTypeFilter.Story ||
        contentTypeFilter === ContentTypeFilter.Story)
    ) {
      void dispatch(setDateRangeAndFetchPosts(startDate, endDate, true));
    }
  },
);

export const updateCalendarUrlParamsFromState = createThunk(
  () => (dispatch, getState) => {
    const dateRange = getCurrentDateRange(getState());
    const view = getScheduleView(getState());
    const networkFilter = getCurrentNetworkFilter(getState());

    updateCalendarUrlParams(dateRange.startDate, dateRange.endDate, view);
    updateCalendarNetworkFilterParam(networkFilter);
  },
);

export const setDateRangeAndFetchPosts = createThunk(
  (start: DateString, end: DateString, shouldClearPosts = false) =>
    async (dispatch) => {
      dispatch(
        actions.setDateRange({
          startDate: start,
          endDate: end,
        }),
      );

      await dispatch(fetchPosts(shouldClearPosts));
    },
);

export const fetchPosts = createThunk(
  (shouldClearPosts = false) =>
    async (dispatch, getState) => {
      const { endDate, startDate } = getCurrentDateRange(getState());

      dispatch(actions.requestScheduledPosts());

      try {
        const { draftPosts, scheduledPosts } = await dispatch(
          getAndParseScheduledItemsInDateRange(startDate, endDate),
        );
        if (shouldClearPosts) {
          dispatch(clearPostsAndDrafts());
        }
        dispatch(
          actions.receiveScheduledPostsSucceeded({
            scheduledPosts,
            draftPosts,
          }),
        );
      } catch (_e) {
        dispatch(
          actions.receiveScheduledPostsFailed(
            '[fetchPosts] Unknown error fetching posts',
          ),
        );
      }
    },
);

const getScheduledPostSet = async (request: {
  postingGroups: UniversalPostingGroupName[];
  postStatuses: PostableStatus[];
  offset: number;
  start: Moment;
  end?: Moment;
}) => {
  const requestParams = {
    after: request.start.toDate(),
    before: request.end?.toDate(),
    groups: request.postingGroups,
    status: request.postStatuses,
    limit: SCHEDULED_POST_FETCH_LIMIT,
    offset: request.offset,
  };

  const { data: scheduledPosts, totalCount } = await searchScheduledPosts({
    ...requestParams,
  });

  return {
    totalCount,
    scheduledPosts,
  };
};

export const fetchSinglePost = createThunk(
  (scheduledPostId: PostableId | undefined, draftPostId: DraftId | undefined) =>
    async (dispatch) => {
      dispatch(actions.requestScheduledPosts());

      try {
        if (isDefined(scheduledPostId)) {
          const updatedPost = await getScheduledPost(scheduledPostId);

          dispatch(actions.receiveScheduledPostByIdSucceeded(updatedPost));
        } else if (isDefined(draftPostId)) {
          const updatedPost = await getDraftPostsByIds([draftPostId]);

          if (updatedPost[0]) {
            dispatch(actions.receiveDraftPostByIdSucceeded(updatedPost[0]));
          }
        }
      } catch (_e) {
        dispatch(
          actions.receiveScheduledPostsFailed(
            '[fetchSinglePost] Unknown error fetching single post',
          ),
        );
      }
    },
);

/**
 * Fetches a complete set of posts for a given date range, or from the start date forwards
 * if no end date is included. If no startDate is included, it uses the current startDate.
 *
 * Loads the associated media items for this post set into
 * the schedule media store, and returns the normalized scheduled posts.
 */
const getScheduledPosts = createThunk(
  (start: Moment, end?: Moment) => async (_dispatch, getState) => {
    const postingGroups = getCurrentPostingGroupsForRequest(getState());

    if (postingGroups.length === 0) {
      return [];
    }

    const postStatuses = getCurrentPostStatusesForRequest(getState());

    const { scheduledPosts, totalCount } = await getScheduledPostSet({
      postingGroups,
      postStatuses,
      offset: 0,
      start,
      end,
    });

    const remainingCount = totalCount - scheduledPosts.length;
    const remainingCalls = Math.ceil(
      remainingCount / SCHEDULED_POST_FETCH_LIMIT,
    );
    const pages = Array.from({ length: remainingCalls }, (_, page) => page);
    const nextScheduledPosts = (
      await Promise.all(
        pages.map(async (page) => {
          const { scheduledPosts: nextPosts } = await getScheduledPostSet({
            postingGroups,
            postStatuses,
            offset: SCHEDULED_POST_FETCH_LIMIT * (page + 1),
            start,
            end,
          });
          return nextPosts;
        }),
      )
    ).flat();

    return [...scheduledPosts, ...nextScheduledPosts];
  },
);

const getDraftPostSet = async (request: {
  postingGroups: UniversalPostingGroupName[];
  offset: number;
  start: Moment;
  end?: Moment;
}) => {
  const requestParams = {
    after: request.start.toDate(),
    before: request.end?.toDate(),
    groups: request.postingGroups,
    limit: SCHEDULED_POST_FETCH_LIMIT,
    offset: request.offset,
  };

  const { data: draftPosts, totalCount } = await searchDraftPosts({
    ...requestParams,
  });

  return {
    totalCount,
    draftPosts,
  };
};

/**
 * Fetches a complete set of drafts for a given date range, or from the start date forwards
 * if no end date is included. If no startDate is included, it uses the current startDate.
 */
const getDraftPosts = createThunk(
  (start: Moment, end?: Moment) => async (_dispatch, getState) => {
    const postingGroups = getCurrentPostingGroupsForRequest(getState());

    if (postingGroups.length === 0) {
      return [];
    }

    const { draftPosts, totalCount } = await getDraftPostSet({
      postingGroups,
      offset: 0,
      start,
      end,
    });

    const remainingCount = totalCount - draftPosts.length;
    const remainingCalls = Math.ceil(
      remainingCount / SCHEDULED_POST_FETCH_LIMIT,
    );
    const pages = Array.from({ length: remainingCalls }, (_, page) => page);
    const nextDraftPosts = (
      await Promise.all(
        pages.map((page) =>
          getDraftPostSet({
            postingGroups,
            offset: SCHEDULED_POST_FETCH_LIMIT * (page + 1),
            start,
            end,
          }),
        ),
      )
    )
      .map((result) => result.draftPosts)
      .flat();

    return [...draftPosts, ...nextDraftPosts];
  },
);

export const fetchCalendarSettingsAndNavigate = createThunk(
  (startDate?: DateString) => async (dispatch) => {
    dispatch(actions.requestCalendarSettings());
    try {
      const { scheduleCalendarStartOfWeek, scheduleCalendarView } =
        await userSettingsApi.getUserSettings();

      const settings = {
        scheduleCalendarStartOfWeek,
        scheduleCalendarView,
      };

      dispatch(actions.receiveCalendarSettingsSucceeded(settings));

      if (isDefined(startDate)) {
        void dispatch(navigateCalendarToDate(startDate));
      } else {
        void dispatch(navigateCalendarToToday());
      }

      return settings;
    } catch (_e) {
      dispatch(
        actions.receiveCalendarSettingsFailed(
          '[fetchCalendarSettings] Unknown error fetching calendar settings',
        ),
      );
    }
  },
);

export const setCalendarSettings = createThunk(
  (settings: IScheduleCalendarSettings) => async (dispatch) => {
    dispatch(actions.requestSetCalendarSettings(settings));
    try {
      await userSettingsApi.setUserSettings(settings);
      dispatch(actions.receiveSetCalendarSettingsSucceeded());
    } catch (_e) {
      dispatch(
        actions.receiveSetCalendarSettingsFailed(
          '[setCalendarSettings] Unknown error setting calendar settings',
        ),
      );
    }
  },
);

export const getAndParseScheduledItemsInDateRange = createThunk(
  (startDate: DateString, endDate: DateString) =>
    async (dispatch, getState) => {
      const tz = getUserTimeZone(getState());
      const [start, end] = [
        DateString.toMoment(startDate, tz),

        // DateString.toMoment gives us the appropriate moment for 12AM on the given date.
        // We need to set this to the endOfDay to ensure we get all of the posts for that day.
        DateString.toMoment(endDate, tz).endOf('day'),
      ];
      try {
        const scheduledPosts = await dispatch(getScheduledPosts(start, end));
        const draftPosts = await dispatch(getDraftPosts(start, end));

        return {
          scheduledPosts: normalize(scheduledPosts, (post) => post.id),
          draftPosts: normalize(draftPosts, (draft) => draft.id),
        };
      } catch (_e) {
        throw new Error('Error getAndParseScheduledItemsInDateRange', {
          cause: _e,
        });
      }
    },
);

export const deleteScheduledPost = createThunk(
  (post: IScheduledPost, onDelete?: () => void) =>
    async (dispatch, getState): Promise<boolean> => {
      const tz = getUserTimeZone(getState());

      dispatch(actions.requestDeletePost(post));

      try {
        await deleteScheduledPosts([post.id]);

        // Update feed preview to match changes made from post details modal
        if (
          getSocialNetworkFromPostType(post.postType) ===
          SocialNetwork.Instagram
        ) {
          invalidateFeedPreviewScheduledInstagramPostsQuery(tz);
        }

        dispatch(actions.receiveDeletePostSucceeded());

        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Deleted Post',
            type: ToastType.SUCCESS,
            message: `Your post has been deleted.`,
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );

        if (isDefined(onDelete)) {
          onDelete();
        }

        return true;
      } catch (_e) {
        dispatch(
          actions.receiveDeletePostFailed({
            post: post,
            errorMessage: `Failed to delete post: ${post.id}`,
          }),
        );

        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Unable to Delete Post',
            type: ToastType.ERROR,
            message:
              'We were unable to delete this post. Please try again later.',
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );

        return false;
      }
    },
);

export const deleteDraftPost = createThunk(
  (
    draft: IDraftPost,
    options: { shouldShowToast: boolean } = { shouldShowToast: true },
    onDelete?: () => void,
  ) =>
    async (dispatch): Promise<boolean> => {
      dispatch(actions.requestDeleteDraftPost(draft));

      try {
        await deleteDraftPosts([draft.id]);

        dispatch(actions.receiveDeleteDraftPostSucceeded());

        if (options.shouldShowToast) {
          dispatch(
            toastNotificationsActions.addToastNotification({
              title: 'Deleted Draft',
              type: ToastType.SUCCESS,
              message: `Your draft has been deleted`,
              options: {
                toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
              },
            }),
          );
        }

        if (isDefined(onDelete)) {
          onDelete();
        }

        return true;
      } catch (_e) {
        dispatch(
          actions.receiveDeleteDraftPostFailed({
            draft,
            errorMessage: `Failed to delete draft post: ${draft.id}`,
          }),
        );

        if (options.shouldShowToast) {
          dispatch(
            toastNotificationsActions.addToastNotification({
              title: 'Unable to Delete Draft',
              type: ToastType.ERROR,
              message: 'We were unable to this draft. Please try again later.',
              options: {
                toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
              },
            }),
          );
        }

        return false;
      }
    },
);

export const updatePostScheduledTime = createThunk(
  (post: IScheduledPost | IDraftPost, newScheduledTime: Date) =>
    async (dispatch, getState): Promise<boolean> => {
      const dateRange = getCurrentDateRange(getState());
      const tz = getUserTimeZone(getState());

      dispatch(
        actions.requestUpdatePostScheduledTime({ post, newScheduledTime }),
      );
      const newScheduledMoment = momentFromDate(newScheduledTime, tz);

      try {
        if (isDraftPost(post)) {
          await updateDraftPost(post.id, post.config, newScheduledTime);
        } else {
          await updateScheduledPost(post.id, {
            ...post,
            scheduledTime: newScheduledTime,
          });

          // Update feed preview to match changes made from calendar
          if (
            getSocialNetworkFromPostType(post.postType) ===
            SocialNetwork.Instagram
          ) {
            invalidateFeedPreviewScheduledInstagramPostsQuery(tz);
          }
        }

        dispatch(actions.receiveUpdatePostScheduledTimeSucceeded());

        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Rescheduled',
            type: ToastType.SUCCESS,
            message: `Your post was rescheduled to ${newScheduledMoment.format(
              'LL',
            )} at ${newScheduledMoment.format('LT')}.`,
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );

        return true;
      } catch (_e) {
        dispatch(
          actions.receiveUpdatePostScheduledTimeFailed({
            post,
            errorMessage: `Failed to reschedule post: ${post.id}`,
          }),
        );

        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Unable to Reschedule Post',
            type: ToastType.ERROR,
            message: 'We were unable to reschedule this post',
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );

        return false;
      }
    },
);

export const updatePostDetails = createThunk(
  (updatedPost: IScheduledPost | IDraftPost) =>
    async (dispatch, getState): Promise<boolean> => {
      const userTimezone = getUserTimeZone(getState());
      const validationErrors = getModalValidationErrors(getState());
      const postInModal = getCurrentPostInModal(getState());

      if (getHasPostedTimeError(updatedPost) && isScheduledPost(updatedPost)) {
        dispatch(
          setValidationErrors([PostDetailsValidationError.PostTimeInPast]),
        );
        return false;
      } else {
        dispatch(
          setValidationErrors(
            difference(validationErrors, [
              PostDetailsValidationError.PostTimeInPast,
            ]),
          ),
        );
      }

      dispatch(actions.requestUpdatePostDetails());

      try {
        if (isDraftPost(updatedPost)) {
          await updateDraftPost(
            updatedPost.id,
            updatedPost.config,
            updatedPost.scheduledTime,
          );
        } else {
          await updateScheduledPost(updatedPost.id, {
            ...updatedPost,
          });

          // Update feed preview to match date changes made from post details modal
          if (
            getSocialNetworkFromPostType(updatedPost.postType) ===
              SocialNetwork.Instagram &&
            updatedPost.scheduledTime !== postInModal?.scheduledTime
          ) {
            invalidateFeedPreviewScheduledInstagramPostsQuery(userTimezone);
          }
        }

        dispatch(actions.receiveUpdatePostDetailsSucceeded());

        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Updated',
            type: ToastType.SUCCESS,
            message: isDefined(updatedPost.config.socialNetwork)
              ? `Your ${updatedPost.config.socialNetwork} post was successfully updated!`
              : 'Your post was successfully updated!',
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );

        // Once we successfully update the post, we want to refresh the calendar
        void dispatch(fetchPosts(true));

        return true;
      } catch (_e) {
        dispatch(
          actions.receiveUpdatePostDetailsFailed(
            `Unknown error updating post ${updatedPost.id}`,
          ),
        );

        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Unable to Update Post',
            type: ToastType.ERROR,
            message:
              'We were unable to update this post. Please try again or contact Bazaarvoice Support.',
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );

        return false;
      }
    },
);

export const navigateCalendarForward = createThunk(
  (shouldUpdateUrlParams?: boolean) => (dispatch, getState) => {
    const startDate = getCurrentStartDate(getState());
    const startOfWeekDay = getStartOfWeek(getState());
    const view = getScheduleView(getState());

    const { endDate: nextEndDate, startDate: nextStartDate } = getViewDates(
      startDate,
      startOfWeekDay,
      view,
      1,
    );

    void dispatch(setDateRangeAndFetchPosts(nextStartDate, nextEndDate));

    if (shouldUpdateUrlParams) {
      updateCalendarUrlParams(nextStartDate, nextEndDate, view);
    }
  },
);

export const navigateCalendarBackward = createThunk(
  (shouldUpdateUrlParams?: boolean) => (dispatch, getState) => {
    const startDate = getCurrentStartDate(getState());
    const startOfWeekDay = getStartOfWeek(getState());
    const view = getScheduleView(getState());

    const { endDate: previousEndDate, startDate: previousStartDate } =
      getViewDates(startDate, startOfWeekDay, view, -1);

    void dispatch(
      setDateRangeAndFetchPosts(previousStartDate, previousEndDate),
    );

    if (shouldUpdateUrlParams) {
      updateCalendarUrlParams(previousStartDate, previousEndDate, view);
    }
  },
);

export const navigateCalendarToDate = createThunk(
  (date: DateString) => (dispatch, getState) => {
    const startOfWeekDay = getStartOfWeek(getState());
    const view = getScheduleView(getState());

    const { endDate, startDate } = getViewDates(date, startOfWeekDay, view);

    void dispatch(setDateRangeAndFetchPosts(startDate, endDate));

    // We need the timeout to ensure this happens after routing
    setTimeout(() => updateCalendarUrlParams(startDate, endDate, view), 10);
  },
);

export const navigateCalendarToToday = createThunk(
  () => (dispatch, getState) => {
    const tz = getUserTimeZone(getState());

    const today = DateString.getToday(tz);
    dispatch(navigateCalendarToDate(today));
  },
);

export const setScheduleViewAndNavigate = createThunk(
  (currentView: UniversalCalendarView, newView: UniversalCalendarView) =>
    (dispatch, getState) => {
      const { endDate, startDate } = getCurrentDateRange(getState());
      const tz = getUserTimeZone(getState());

      const today = DateString.getToday(tz);
      const isCurrentMonthThisMonth =
        DateString.parse(today).month ===
        DateString.parse(getStartOfCurrentMonth(startDate)).month;
      const areStartAndEndDatesInSameMonth =
        DateString.parse(startDate).month === DateString.parse(endDate).month;

      dispatch(setScheduleView(newView));

      if (
        isCurrentMonthThisMonth &&
        currentView === UniversalCalendarView.Month
      ) {
        dispatch(navigateCalendarToDate(today));
      } else if (
        !areStartAndEndDatesInSameMonth &&
        newView === UniversalCalendarView.Month
      ) {
        dispatch(navigateCalendarToDate(endDate));
      } else {
        dispatch(navigateCalendarToDate(startDate));
      }

      void dispatch(
        setCalendarSettings({
          scheduleCalendarView: newView,
        }),
      );
    },
);

export const resetFilters = createThunk(() => (dispatch, getState) => {
  dispatch(setSearchText(''));
  dispatch(setNetworkFilter('All'));
  dispatch(setPostStatusFilter('All'));
  dispatch(setContentTypeFilter('All'));
});
